import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import {
  AlertColor,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import DrawerMenu from 'components/DrawerMenu';
import Form, { FormDataModel } from 'components/Form';
import Notification from 'components/Notification';
import PageLoader from 'components/PageLoader';
import Section from 'components/Section/Section';
import dayjs from 'dayjs';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import { FormComponent } from 'utils/validators/HelperComponents';
import { getDrawerWidth, removeErrorFieldsFromValues } from 'utils/validators/HelperFunctions';
import EventDetailCard, { eventStartMinutes, formatBeoDate, formatTimeRange } from './EventDetailCard';
import { EventDetailForm } from './Forms/EventDetailForm';

const compareByDateThenTime = (aDate: any, aEvent: any, bDate: any, bEvent: any) => {
  const da = dayjs(aDate);
  const db = dayjs(bDate);
  const dateCmp = (da.isValid() ? da.format('YYYY-MM-DD') : '').localeCompare(
    db.isValid() ? db.format('YYYY-MM-DD') : '',
  );
  if (dateCmp !== 0) return dateCmp;
  return eventStartMinutes(aEvent) - eventStartMinutes(bEvent);
};

export const EventDetailsTable = ({
  selectedEventDetails,
  setSelectedEventDetails,
  sentToStaff,
  isPastBooking,
}: any) => {
  const theme = useTheme();
  const { id } = useParams();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [pageResponse, setPageResponse] = useState<any>({});
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [eventDetailsList, setEventDetailsList] = useState<any[]>([]);
  const [layoutList, setLayoutList] = useState<any[]>([]);
  const [addOnsEvent, setAddOnsEvent] = useState<any>(null);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  let formRef: RefObject<Form | null | undefined> = useRef();
  const readOnly = Boolean(sentToStaff || isPastBooking);

  const getInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(URLS.beoDropdown(id));
      setEventDetailsList(data || []);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      if (error.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response.data?.error_description ?? error.response.data.message,
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: error.message,
        });
      }
    }
  }, [id]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const handleSave = (e: any) => {
    e.preventDefault();
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const tempData = removeErrorFieldsFromValues(formData);
    setHasError(false);
    if (isFormValid) {
      const array = [...selectedEventDetails];
      const venueData = eventDetailsList.find((item: any) => item.id === tempData.bookingLineItemId);
      const data = {
        ...tempData,
        eventDate: venueData.date,
        venue: venueData.venue,
        selectedLayout:
          tempData.layoutId === '' ? null : venueData.layouts.find((item: any) => item.id === tempData.layoutId)?.name,
        timeSlot: formatTimeRange(venueData) || venueData.timeSlot,
        startTime: venueData.startTime,
        endTime: venueData.endTime,
        guaranteedGuests: tempData.guaranteedGuests,
        eventType: tempData.eventType || venueData.eventType,
        eventSubtype: tempData.eventSubtype || venueData.eventSubtype,
        commercials: venueData.commercials,
      };
      if (selectedEventDetails.findIndex((item: any) => item.bookingLineItemId === tempData.bookingLineItemId) !== -1) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.editEventDetailErrorMsg,
        });
        return;
      }
      array.push(data);
      setSelectedEventDetails(array);
      setOpenDrawer(false);
      setPageResponse({});
    } else {
      setHasError(true);
    }
  };

  const handleClose = () => {
    setOpenDrawer(false);
    setPageResponse({});
    formRef.current?.resetForm();
    setHasError(false);
  };

  const deleteRow = (idx: any) => {
    const deleteValue = selectedEventDetails.filter((item: any, i: any) => i !== idx);
    setSelectedEventDetails(deleteValue);
  };
  const handleChange = (field: string, value: any, formData: any) => {
    if (field === 'bookingLineItemId') {
      const venueData = eventDetailsList.find((item: any) => item.id === value);
      setLayoutList(
        venueData.layouts.map((item: any) => ({
          value: item.id,
          label: item.name,
        })),
      );
      setPageResponse({
        bookingLineItemId: value,
        layoutId: venueData.selectedLayout?.value,
        timeSlot: formatTimeRange(venueData) || venueData.timeSlot,
        startTime: venueData.startTime,
        endTime: venueData.endTime,
        guaranteedGuests: venueData.guaranteedGuests,
        eventType: venueData.eventType,
        eventSubtype: venueData.eventSubtype,
      });
    }
  };

  const canDeleteEvent = (event: any) => !(event.id && event.id.toString() === id?.toString());

  const addedLineItemIds = useMemo(() => {
    return new Set(
      (selectedEventDetails || [])
        .map((event: any) => event.bookingLineItemId ?? event.id)
        .filter((lineItemId: any) => lineItemId != null)
        .map((lineItemId: any) => String(lineItemId)),
    );
  }, [selectedEventDetails]);

  const unaddedEventDetails = useMemo(() => {
    return (eventDetailsList || [])
      .filter((item: any) => !addedLineItemIds.has(String(item.id)))
      .sort((a: any, b: any) => compareByDateThenTime(a.date, a, b.date, b));
  }, [eventDetailsList, addedLineItemIds]);

  const venueList = useMemo(() => {
    return unaddedEventDetails.map((d: any) => {
      const subtype = d.eventSubtype && String(d.eventSubtype).trim();
      return {
        value: d.id,
        label: [d.venue, subtype].filter(Boolean).join(' · '),
        secondary: [formatBeoDate(d.date), formatTimeRange(d)].filter(Boolean).join(' · '),
      };
    });
  }, [unaddedEventDetails]);

  const canAddEvent = !readOnly && unaddedEventDetails.length > 0;

  const orderedEvents: { event: any; index: number }[] = useMemo(() => {
    return (selectedEventDetails || [])
      .map((event: any, index: number) => ({ event, index }))
      .sort((a: { event: any; index: number }, b: { event: any; index: number }) =>
        compareByDateThenTime(a.event.eventDate, a.event, b.event.eventDate, b.event),
      );
  }, [selectedEventDetails]);

  return (
    <>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={5000}
        />
      )}
      <Box sx={{ mb: 2.5 }}>
        <Section
          label={strings.new_booking_section_event_details}
          icon={<EventNoteOutlinedIcon fontSize="small" />}
          compact={false}
        />
        {canAddEvent && (
          <Button
            onClick={() => setOpenDrawer(true)}
            variant="text"
            sx={{ '.MuiButton-startIcon': { marginRight: '6px' }, minHeight: '36px', mt: 0.5, textTransform: 'none', px: 0 }}
            startIcon={<AddCircleOutline />}>
            {strings.beo_add_event}
          </Button>
        )}
        <Stack spacing={1} sx={{ mt: 1 }}>
          {orderedEvents.map(({ event, index }) => (
            <EventDetailCard
              key={`${event.bookingLineItemId ?? index}`}
              event={event}
              readOnly={readOnly}
              canDelete={canDeleteEvent(event)}
              onDelete={() => deleteRow(index)}
              onViewAddOns={() => setAddOnsEvent(event)}
            />
          ))}
        </Stack>
      </Box>
      <DrawerMenu
        width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
        open={openDrawer}
        handleClose={handleClose}
        closeButton={true}
        dropBack={false}
        title={strings.beo_add_event_detail}>
        <FormComponent
          handleChange={handleChange}
          hasError={hasError}
          formRef={formRef}
          pageResponse={pageResponse}
          handleSave={handleSave}
          formModel={EventDetailForm(strings, venueList, layoutList)}
        />
      </DrawerMenu>
      <DrawerMenu
        width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
        open={Boolean(addOnsEvent)}
        handleClose={() => setAddOnsEvent(null)}
        closeButton={true}
        dropBack={false}
        title={strings.beo_event_add_ons}>
        {addOnsEvent?.commercials?.addOnPrice ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {strings.beo_event_add_ons_price}: {addOnsEvent.commercials.addOnPrice}
          </Typography>
        ) : null}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{strings.beo_add_on_name}</TableCell>
              <TableCell align="right">{strings.beo_add_on_quantity}</TableCell>
              <TableCell align="right">{strings.beo_add_on_price}</TableCell>
              <TableCell align="right">{strings.beo_add_on_total}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(addOnsEvent?.commercials?.addOns || []).map((addOn: any, addOnIndex: number) => (
              <TableRow key={`${addOn.name ?? 'addon'}-${addOnIndex}`}>
                <TableCell>{addOn.name}</TableCell>
                <TableCell align="right">{addOn.quantity}</TableCell>
                <TableCell align="right">{addOn.price}</TableCell>
                <TableCell align="right">{addOn.total}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DrawerMenu>
    </>
  );
};
